'use client';

import { useMemo } from 'react';
import { VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import type { InternalNetworkCredentialDto } from '@terra/shared-contracts-internal-network';
import { formatAdCredentialIdentity, automationId } from '@terra/shared-utils';
import { FieldWrapper } from '../../../components/field-wrapper/FieldWrapper';
import { Input } from '../../../components/input';
import { MetaDefault } from '../../../components/typography';
import { SettingsEditDialog } from '../../settings/SettingsEditDialog';
import { PasswordField } from '../../inputs/PasswordField';
import { useDraftOnOpen } from '../../application-general/useDraftOnOpen';
import { InternalNetworkCredentialSelectField } from './InternalNetworkCredentialSelectField';
import {
  INTERNAL_NETWORK_CREDENTIAL_AUTH_METHODS,
  INTERNAL_NETWORK_CREDENTIAL_TYPES,
  applyInternalNetworkCredentialUsername,
  createInternalNetworkCredentialForm,
  isInternalNetworkCredentialAuthMethod,
  isInternalNetworkCredentialFormSubmitDisabled,
  isInternalNetworkCredentialType,
  toInternalNetworkCredentialSaveBody,
  usesAzureCredential,
  usesNtlmHashCredential,
  type InternalNetworkCredentialSaveBody,
  type InternalNetworkCredentialType,
} from './internal-credentials.model';

export type AddInternalNetworkCredentialDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialCredential?: InternalNetworkCredentialDto | null;
  defaultDomain?: string;
  defaultCredentialType?: InternalNetworkCredentialType;
  loading: boolean;
  onSave: (body: InternalNetworkCredentialSaveBody) => Promise<void>;
};

export function AddInternalNetworkCredentialDialog({
  open,
  onOpenChange,
  initialCredential,
  defaultDomain,
  defaultCredentialType,
  loading,
  onSave,
}: AddInternalNetworkCredentialDialogProps) {
  const t = useTranslations('designSystem.networkSettings.internalCredentials');
  const tCommon = useTranslations('designSystem.common');
  const [form, setForm] = useDraftOnOpen({
    open,
    value: createInternalNetworkCredentialForm({
      initialCredential,
      defaultDomain,
      defaultCredentialType,
    }),
  });
  const credentialTypeItems = useMemo(
    () =>
      INTERNAL_NETWORK_CREDENTIAL_TYPES.map((credentialType) => ({
        label: t(`credentialTypes.${credentialType}`),
        value: credentialType,
      })),
    [t],
  );
  const authMethodItems = useMemo(
    () =>
      INTERNAL_NETWORK_CREDENTIAL_AUTH_METHODS.map((authMethod) => ({
        label: t(`authMethods.${authMethod}`),
        value: authMethod,
      })),
    [t],
  );
  const isEditing = !!initialCredential;
  const usesHash = usesNtlmHashCredential(form.credentialType);
  const usesAzure = usesAzureCredential(form.credentialType);
  const showIdentityPreview = !usesAzure && form.username.trim().length > 0;
  const identityPreview = formatAdCredentialIdentity({
    username: form.username,
    domain: form.domain,
  });

  return (
    <SettingsEditDialog
      open={open}
      title={isEditing ? t('editCredentialTitle') : t('addCredentialTitle')}
      contentMaxHeight="min(55vh, 520px)"
      onOpenChange={onOpenChange}
      onSubmit={() => void onSave(toInternalNetworkCredentialSaveBody(form))}
      loading={loading}
      isSubmitDisabled={isInternalNetworkCredentialFormSubmitDisabled({
        form,
        isEditing,
      })}
      submitText={isEditing ? tCommon('saveChanges') : tCommon('add')}
      contentAutomationId="internal-network-credential-dialog"
      submitAutomationId="internal-network-credential-dialog-submit"
    >
      <VStack alignItems="stretch" gap={4} w="full">
        <FieldWrapper label={t('nameLabel')}>
          <Input
            size="sm"
            placeholder={t('namePlaceholder')}
            value={form.name}
            onChange={(event) =>
              setForm((current) => ({ ...current, name: event.target.value }))
            }
            {...automationId('internal-network-credential-name-input')}
          />
        </FieldWrapper>
        <FieldWrapper
          label={t('username')}
          helperText={
            usesAzure ? t('azureUsernameHelper') : t('usernameHelper')
          }
        >
          <Input
            size="sm"
            placeholder={t('usernamePlaceholder')}
            value={form.username}
            onChange={(event) =>
              setForm((current) =>
                applyInternalNetworkCredentialUsername({
                  form: current,
                  value: event.target.value,
                }),
              )
            }
            {...automationId('internal-network-credential-username-input')}
          />
        </FieldWrapper>
        <FieldWrapper
          label={usesAzure ? t('azureTenantId') : t('domain')}
          helperText={usesAzure ? t('azureTenantIdHelper') : t('domainHelper')}
        >
          <Input
            size="sm"
            placeholder={
              usesAzure ? t('azureTenantIdPlaceholder') : t('domainPlaceholder')
            }
            value={form.domain}
            onChange={(event) =>
              setForm((current) => ({ ...current, domain: event.target.value }))
            }
            {...automationId('internal-network-credential-domain-input')}
          />
        </FieldWrapper>
        {showIdentityPreview ? (
          <MetaDefault color="fg.muted">
            {t('identityPreview', { identity: identityPreview })}
          </MetaDefault>
        ) : null}
        {usesHash ? (
          <PasswordField
            label={t('ntlmHash')}
            placeholder={t('ntlmHashPlaceholder')}
            value={form.ntlmHash}
            onChange={(value) =>
              setForm((current) => ({ ...current, ntlmHash: value }))
            }
            hidePasswordToggle={isEditing}
            isRequired={!isEditing}
          />
        ) : (
          <PasswordField
            label={usesAzure ? t('azureRefreshToken') : t('passwordLabel')}
            placeholder={
              usesAzure
                ? t('azureRefreshTokenPlaceholder')
                : t('passwordPlaceholder')
            }
            value={form.password}
            onChange={(value) =>
              setForm((current) => ({ ...current, password: value }))
            }
            hidePasswordToggle={isEditing}
            isRequired={!isEditing}
            dataTestid="internal-network-credential-password-input"
          />
        )}
        <InternalNetworkCredentialSelectField
          label={t('credentialType')}
          value={form.credentialType}
          items={credentialTypeItems}
          isValue={isInternalNetworkCredentialType}
          onValueChange={(credentialType) =>
            setForm((current) => ({ ...current, credentialType }))
          }
        />
        {!usesHash && !usesAzure ? (
          <InternalNetworkCredentialSelectField
            label={t('authMethod')}
            helperText={t('authMethodHelper')}
            value={form.authMethod}
            items={authMethodItems}
            isValue={isInternalNetworkCredentialAuthMethod}
            onValueChange={(authMethod) =>
              setForm((current) => ({ ...current, authMethod }))
            }
          />
        ) : null}
      </VStack>
    </SettingsEditDialog>
  );
}
